import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { sanitizeSectionHtml } from '../../utils/sanitizeSectionHtml';

/**
 * Plain contentEditable region for Protocol section HTML — no formatting toolbar.
 * The user edits the rendered content directly (headings look like headings, tables
 * look like tables); typing only changes the text node under the caret, and Enter
 * splits/creates <p> tags the way a browser normally does. This replaces the old
 * flatten-to-text + string-splice approach, which corrupted structure on save.
 *
 * The DOM is only reset from `html` when `resetKey` changes (section switch / external
 * update) — never on every render — so the caret position isn't lost while typing.
 */
const EditableSectionContent = ({ html, resetKey, onChange, onBlur, sx, placeholder }) => {
  const ref = useRef(null);
  const lastResetKey = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    if (lastResetKey.current === resetKey) return;
    lastResetKey.current = resetKey;
    // Sanitize on load too, not just on the way back out via handleInput —
    // `html` is stored/AI-generated Protocol content and this was setting it
    // straight into the live DOM unsanitized on every section switch.
    ref.current.innerHTML = sanitizeSectionHtml(html || '');
  }, [resetKey, html]);

  const handleInput = (e) => {
    const rawHtml = e.currentTarget.innerHTML;
    onChange(sanitizeSectionHtml(rawHtml));
  };

  return (
    <Box
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      // Leaving the section ends the edit — the parent saves right away instead of
      // waiting out its idle timer.
      onBlur={onBlur}
      data-placeholder={placeholder}
      sx={{
        minHeight: 200,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 3,
        fontFamily: 'Times New Roman, serif',
        fontSize: 14,
        color: '#333333',
        lineHeight: 1.6,
        outline: 'none',
        '&:focus': {
          borderColor: '#168AAD',
          boxShadow: '0 0 0 2px rgba(22, 138, 173, 0.15)',
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontWeight: 'bold',
          color: '#2c3e50',
          mt: 2,
          mb: 1,
        },
        '& h1': { fontSize: '1.5em' },
        '& h2': { fontSize: '1.3em' },
        '& h3': { fontSize: '1.1em' },
        '& p': { mb: 1 },
        '& ul, & ol': { pl: 2, mb: 1 },
        '& li': { mb: 0.5 },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          mb: 2,
        },
        '& th, & td': {
          border: '1px solid #ddd',
          padding: '8px',
          textAlign: 'left',
        },
        '& th': {
          backgroundColor: '#f8f9fa',
          fontWeight: 'bold',
        },
        ...sx,
      }}
    />
  );
};

export default EditableSectionContent;
